import dayjs from "dayjs";
import da from "dayjs/locale/da";
import { FinanceCategory, FinanceTransaction } from "../types/entities";

dayjs.locale(da);

/**
 * Everything the finance screen knows how to do to a pile of transactions,
 * kept away from the components that draw it. The chart, the scrubber and
 * the list all read the same monthly series, so "what was the balance in
 * June" has exactly one answer no matter which of them is asking.
 */

/** One month of the ledger, with the balance as it stood when it ended. */
export interface MonthPoint {
  /** Sort/lookup key, `YYYY-MM`. */
  key: string;
  /** Midnight on the 1st — the x value the chart plots against. */
  date: Date;
  /** "juni 26", the way the spreadsheet labels its rows. */
  label: string;
  /** Money in, in øre. Always >= 0. */
  incomeOre: number;
  /** Money out as a magnitude, in øre. Always >= 0 — the sign lives in the label, not the number. */
  expenseOre: number;
  /** incomeOre - expenseOre. */
  netOre: number;
  /** The balance at the *end* of this month, opening balance included. */
  balanceOre: number;
  /** This month's transactions, newest first. Excludes the opening balance. */
  transactions: FinanceTransaction[];
}

export const CATEGORY_LABELS: Record<FinanceCategory, string> = {
  primo: "Primo",
  kontingent: "Kontingent",
  bode: "Bøde",
  rente: "Rente",
  arrangement: "Arrangement",
  gebyr: "Gebyr",
  andet: "Andet",
};

/** The categories an admin can actually book. `primo` is the ledger's starting
 *  point, written once by the import — offering it in a form would invite a
 *  second opening balance, which the unique index refuses anyway. */
export const BOOKABLE_CATEGORIES: FinanceCategory[] = [
  "kontingent",
  "bode",
  "rente",
  "arrangement",
  "gebyr",
  "andet",
];

/** Which way a category normally points, used to preselect the sign in the
 *  entry form. Never enforced: a refunded deposit is income booked against
 *  `arrangement`, and the ledger should be able to say so. */
export const CATEGORY_DEFAULT_DIRECTION: Record<FinanceCategory, 1 | -1> = {
  primo: 1,
  kontingent: 1,
  bode: 1,
  rente: 1,
  arrangement: -1,
  gebyr: -1,
  andet: -1,
};

export const isOpeningBalance = (t: FinanceTransaction) => t.category === "primo";

const monthKey = (isoDate: string) => isoDate.slice(0, 7);

/** Newest first by date, and by id within a day so rows booked on the same
 *  date keep the order they were entered in. The order every list reads in. */
export const newestFirst = (a: FinanceTransaction, b: FinanceTransaction) =>
  b.occurred_on.localeCompare(a.occurred_on) || b.id - a.id;

/**
 * Groups transactions into a continuous monthly series with a running
 * balance, oldest first.
 *
 * Two things are deliberate. The opening balance (`primo`) is not a month of
 * its own — it is where the running total starts, so the series begins at
 * the first month that actually saw movement, exactly like the spreadsheet's
 * first row. And months with no transactions are still emitted: a quiet
 * March is a flat segment of the line, not a missing one, and dropping it
 * would bunch the remaining months up and make the x axis lie about time.
 */
export function buildMonthlySeries(transactions: FinanceTransaction[]): MonthPoint[] {
  const live = transactions.filter((t) => !t.deleted);
  const movements = live.filter((t) => !isOpeningBalance(t));
  const openingOre = live
    .filter(isOpeningBalance)
    .reduce((sum, t) => sum + t.amount_ore, 0);

  if (movements.length === 0) return [];

  const byMonth = new Map<string, FinanceTransaction[]>();
  for (const t of movements) {
    const key = monthKey(t.occurred_on);
    const bucket = byMonth.get(key);
    if (bucket) bucket.push(t);
    else byMonth.set(key, [t]);
  }

  const keys = Array.from(byMonth.keys()).sort();
  const first = dayjs(`${keys[0]}-01`);
  const last = dayjs(`${keys[keys.length - 1]}-01`);

  const points: MonthPoint[] = [];
  let balanceOre = openingOre;
  for (let cursor = first; !cursor.isAfter(last, "month"); cursor = cursor.add(1, "month")) {
    const key = cursor.format("YYYY-MM");
    const rows = (byMonth.get(key) ?? []).slice().sort(newestFirst);

    let incomeOre = 0;
    let expenseOre = 0;
    for (const t of rows) {
      if (t.amount_ore >= 0) incomeOre += t.amount_ore;
      else expenseOre += -t.amount_ore;
    }
    balanceOre += incomeOre - expenseOre;

    points.push({
      key,
      date: cursor.toDate(),
      label: `${cursor.format("MMMM")} ${cursor.format("YY")}`,
      incomeOre,
      expenseOre,
      netOre: incomeOre - expenseOre,
      balanceOre,
      transactions: rows,
    });
  }
  return points;
}

export type Range = "ytd" | "1y" | "5y" | "all";

/** The conventional finance abbreviations rather than Danish, because that is
 *  what these controls say in every app that has them. */
export const RANGE_LABELS: Record<Range, string> = {
  ytd: "YTD",
  "1y": "1Y",
  "5y": "5Y",
  all: "All",
};

/** How far back each range reaches, in whole months before the current one.
 *  YTD is the odd one out: it counts from January rather than a fixed
 *  distance, so it is handled on its own below. */
const RANGE_MONTHS: Record<Exclude<Range, "ytd" | "all">, number> = {
  "1y": 11,
  "5y": 59,
};

/**
 * Narrows a series to a range *for display only* — every point keeps the
 * balance it had in the full series. Recomputing the running total from the
 * first visible month would show the loge as owning nothing last January,
 * which is the classic way a chart like this ends up lying.
 */
export function sliceRange(points: MonthPoint[], range: Range, now = new Date()): MonthPoint[] {
  if (range === "all") return points;
  const cutoff =
    range === "ytd"
      ? dayjs(now).startOf("year")
      : // Inclusive of the current month, so 1Y is twelve months on the chart
        // rather than thirteen.
        dayjs(now).startOf("month").subtract(RANGE_MONTHS[range], "month");
  const inRange = points.filter((p) => !dayjs(p.date).isBefore(cutoff, "month"));
  // A range that would leave the chart with a single point (or none) has
  // nothing to draw a line between, so fall back to showing everything
  // rather than an empty box. Happens on a young ledger, not a full one.
  return inRange.length >= 2 ? inRange : points;
}

/** One movement, and what the balance stood at once it had happened. */
export interface LedgerEntry {
  transaction: FinanceTransaction;
  balanceOre: number;
}

/** A year of the ledger, for the sectioned list. */
export interface YearGroup {
  /** "2026". */
  year: string;
  /** Newest first — the order the list reads in. */
  entries: LedgerEntry[];
  /** Money in and out over the year. Both exclude the opening balance: it is
   *  where the ledger starts, not something that moved. */
  incomeOre: number;
  expenseOre: number;
  netOre: number;
  /** The balance the year ended on. */
  closingBalanceOre: number;
}

/**
 * The whole ledger as year sections, newest year first, each movement
 * carrying the balance as it stood once that movement had happened.
 *
 * The running total is accumulated oldest-first — the only order in which it
 * means anything — and the result is reversed for reading afterwards, so a
 * row's balance is the same number whichever way the list is displayed.
 *
 * The opening balance appears as a row of its own rather than being folded
 * away. It is where the money came from, and a ledger whose first screen
 * can't say that invites the question every time.
 */
export function groupByYear(transactions: FinanceTransaction[]): YearGroup[] {
  const ordered = transactions
    .filter((t) => !t.deleted)
    .slice()
    .sort((a, b) => a.occurred_on.localeCompare(b.occurred_on) || a.id - b.id);

  const groups = new Map<string, YearGroup>();
  let balanceOre = 0;
  for (const transaction of ordered) {
    balanceOre += transaction.amount_ore;
    const year = transaction.occurred_on.slice(0, 4);

    let group = groups.get(year);
    if (!group) {
      group = {
        year,
        entries: [],
        incomeOre: 0,
        expenseOre: 0,
        netOre: 0,
        closingBalanceOre: balanceOre,
      };
      groups.set(year, group);
    }

    group.entries.push({ transaction, balanceOre });
    if (!isOpeningBalance(transaction)) {
      if (transaction.amount_ore >= 0) group.incomeOre += transaction.amount_ore;
      else group.expenseOre += -transaction.amount_ore;
      group.netOre = group.incomeOre - group.expenseOre;
    }
    // Every row moves the year's closing balance, the opening balance
    // included — that one isn't a movement, but it is still money the year
    // ended holding.
    group.closingBalanceOre = balanceOre;
  }

  return Array.from(groups.values())
    .sort((a, b) => b.year.localeCompare(a.year))
    .map((group) => ({ ...group, entries: group.entries.slice().reverse() }));
}

/** The balance the ledger currently stands at, opening balance included. */
export function currentBalanceOre(transactions: FinanceTransaction[]): number {
  return transactions
    .filter((t) => !t.deleted)
    .reduce((sum, t) => sum + t.amount_ore, 0);
}

// ── Money formatting ────────────────────────────────────────────────────
// Written out rather than handed to Intl: the app runs on iOS web views of
// varying vintage, and the exact shape of `kr 24.173,38` — the space, the
// dot grouping, the comma — is part of the design rather than a locale
// preference. Same reason the roulette wheel doesn't ask the platform what a
// wheel looks like.

const groupThousands = (digits: string) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

export interface KrFormat {
  /** "+" or "−" (a real minus sign), or "" when `signed` is off. */
  sign: string;
  /** "24.173" — grouped, no decimals. */
  whole: string;
  /** "38" — always two digits. */
  decimals: string;
}

/** Splits an amount into its parts so a component can style them separately —
 *  the øre are set smaller than the kroner everywhere they appear. */
export function splitKr(amountOre: number, signed = false): KrFormat {
  const rounded = Math.round(Math.abs(amountOre));
  const whole = Math.floor(rounded / 100);
  const decimals = rounded % 100;
  return {
    sign: signed ? (amountOre < 0 ? "−" : "+") : amountOre < 0 ? "−" : "",
    whole: groupThousands(String(whole)),
    decimals: String(decimals).padStart(2, "0"),
  };
}

/** "kr 24.173,38" / "−kr 2.250,00" / "+1.400,00". */
export function formatKr(
  amountOre: number,
  { signed = false, prefix = true }: { signed?: boolean; prefix?: boolean } = {}
): string {
  const { sign, whole, decimals } = splitKr(amountOre, signed);
  return `${sign}${prefix ? "kr " : ""}${whole},${decimals}`;
}

/** "24,2k" — for axis ticks, where the full number never fits. */
export function formatKrCompact(amountOre: number): string {
  const kr = amountOre / 100;
  const abs = Math.abs(kr);
  if (abs >= 1000) {
    const thousands = kr / 1000;
    // One decimal below 100k, none above — "24,2k" reads, "124,3k" crowds.
    const text = Math.abs(thousands) >= 100
      ? String(Math.round(thousands))
      : thousands.toFixed(1).replace(".", ",");
    return `${text}k`;
  }
  return String(Math.round(kr));
}

/**
 * Reads what someone typed into the amount field, in øre.
 *
 * Danish keyboards put a comma on the decimal key and the app's own output
 * uses dots for thousands, so "1.400,50" is the shape the treasurer will
 * type back in. But phone keypads vary, people paste from bank statements,
 * and plenty of people type "1400.50" out of habit — so the separators are
 * worked out from the string rather than assumed:
 *
 *   "1.400,50" → both present, so the comma is the decimal one
 *   "1400,50"  → a lone comma is always decimal
 *   "1.400"    → a lone dot in a thousands-shaped group is a group
 *   "1400.50"  → a lone dot anywhere else is decimal
 *
 * Returns null for anything that isn't a number, so the form can stay
 * disabled rather than book a NaN.
 */
export function parseKrToOre(input: string): number | null {
  const cleaned = input.replace(/\s|kr\.?/gi, "").replace(/−/g, "-");
  if (!cleaned || !/^-?[\d.,]+$/.test(cleaned)) return null;

  const hasComma = cleaned.includes(",");
  const hasDot = cleaned.includes(".");
  let normalised: string;
  if (hasComma && hasDot) {
    normalised = cleaned.replace(/\./g, "").replace(",", ".");
  } else if (hasComma) {
    normalised = cleaned.replace(",", ".");
  } else if (hasDot && /^-?\d{1,3}(\.\d{3})+$/.test(cleaned)) {
    normalised = cleaned.replace(/\./g, "");
  } else {
    normalised = cleaned;
  }

  const kroner = Number(normalised);
  if (!Number.isFinite(kroner)) return null;
  return Math.round(kroner * 100);
}

/** The inverse, for putting an existing amount back into the field. */
export function oreToInput(amountOre: number): string {
  const { whole, decimals } = splitKr(amountOre);
  return `${whole},${decimals}`;
}
